import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

/**
 * Tooltip Component (Signal)
 *
 * Floating layer: overlay surface, float shadow, 8 px radius. Every `theme`
 * resolves to the same overlay look; motion is skipped under reduced motion.
 */
const Tooltip = ({
  children,
  content,
  position = "top",
  delay = 200,
  arrow = true,
  className = "",
  tooltipClassName = "",
  disabled = false,
  maxWidth = 250,
  triggerOnClick = false,
  closeOnClickOutside = true,
  // eslint-disable-next-line no-unused-vars
  theme = "glass",
  glassMorphism = true,
  animation = "fade",
  offset = 10,
  interactive = false,
  ...props
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const targetRef = useRef(null);
  const tooltipRef = useRef(null);
  const timerRef = useRef(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Add event listeners for click outside
  useEffect(() => {
    if (isVisible && triggerOnClick && closeOnClickOutside) {
      const handleClickOutside = (e) => {
        if (
          targetRef.current &&
          !targetRef.current.contains(e.target) &&
          tooltipRef.current &&
          !tooltipRef.current.contains(e.target)
        ) {
          setIsVisible(false);
        }
      };

      document.addEventListener("click", handleClickOutside);
      return () => {
        document.removeEventListener("click", handleClickOutside);
      };
    }
  }, [isVisible, triggerOnClick, closeOnClickOutside]);

  // Calculate tooltip position based on target element
  const calculatePosition = () => {
    if (!targetRef.current || !tooltipRef.current) return;

    const targetRect = targetRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;

    let top = 0;
    let left = 0;

    const gap = arrow ? offset + 5 : offset;

    switch (position) {
      case "top":
        top = targetRect.top + scrollY - tooltipRect.height - gap;
        left =
          targetRect.left +
          scrollX +
          targetRect.width / 2 -
          tooltipRect.width / 2;
        break;
      case "bottom":
        top = targetRect.bottom + scrollY + gap;
        left =
          targetRect.left +
          scrollX +
          targetRect.width / 2 -
          tooltipRect.width / 2;
        break;
      case "left":
        top =
          targetRect.top +
          scrollY +
          targetRect.height / 2 -
          tooltipRect.height / 2;
        left = targetRect.left + scrollX - tooltipRect.width - gap;
        break;
      case "right":
        top =
          targetRect.top +
          scrollY +
          targetRect.height / 2 -
          tooltipRect.height / 2;
        left = targetRect.right + scrollX + gap;
        break;
      default:
        top = targetRect.top + scrollY - tooltipRect.height - gap;
        left =
          targetRect.left +
          scrollX +
          targetRect.width / 2 -
          tooltipRect.width / 2;
    }

    // Ensure tooltip stays within viewport bounds
    const padding = 10;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Adjust horizontal position if needed
    if (left < padding) {
      left = padding;
    } else if (left + tooltipRect.width > viewportWidth - padding) {
      left = viewportWidth - tooltipRect.width - padding;
    }

    // Adjust vertical position if needed
    if (top < padding + scrollY) {
      top = targetRect.bottom + scrollY + gap; // Flip to bottom
    } else if (top + tooltipRect.height > viewportHeight + scrollY - padding) {
      top = targetRect.top + scrollY - tooltipRect.height - gap; // Flip to top
    }

    setTooltipPosition({ top, left });
  };

  // Show tooltip with delay
  const handleMouseEnter = () => {
    if (disabled || triggerOnClick) return;

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      setIsVisible(true);
      // Wait for the next frame to ensure tooltip is rendered
      requestAnimationFrame(calculatePosition);
    }, delay);
  };

  // Hide tooltip
  const handleMouseLeave = () => {
    if (disabled || triggerOnClick) return;

    if (timerRef.current) clearTimeout(timerRef.current);

    if (!interactive) {
      timerRef.current = setTimeout(() => {
        setIsVisible(false);
      }, 100);
    }
  };

  // Toggle tooltip on click
  const handleClick = (e) => {
    if (disabled || !triggerOnClick) return;

    e.stopPropagation();
    setIsVisible(!isVisible);

    // Wait for the next frame to ensure tooltip is rendered
    if (!isVisible) {
      requestAnimationFrame(calculatePosition);
    }
  };

  // Adjust position when tooltip becomes visible
  useEffect(() => {
    if (isVisible) {
      // Initial calculation
      calculatePosition();

      // Recalculate on scroll and resize
      const handleScroll = () => {
        requestAnimationFrame(calculatePosition);
      };

      const handleResize = () => {
        requestAnimationFrame(calculatePosition);
      };

      window.addEventListener("scroll", handleScroll);
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [isVisible]);

  const reduceMotion = useReducedMotion();

  // `theme` is accepted for compatibility; floating layers share one surface.
  // Every theme renders as strong glass (styles/signal-components.css).
  const getThemeClasses = () => "sg-tooltip";

  // Animation variants
  const animationVariants = {
    fade: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
    },
    scale: {
      initial: { opacity: 0, scale: 0.98 },
      animate: { opacity: 1, scale: 1 },
      exit: { opacity: 0, scale: 0.98 },
    },
    slide: {
      initial: {
        opacity: 0,
        y: position === "top" ? 4 : position === "bottom" ? -4 : 0,
        x: position === "left" ? 4 : position === "right" ? -4 : 0,
      },
      animate: { opacity: 1, y: 0, x: 0 },
      exit: {
        opacity: 0,
        y: position === "top" ? 4 : position === "bottom" ? -4 : 0,
        x: position === "left" ? 4 : position === "right" ? -4 : 0,
      },
    },
    // Signal has no springs: bounce falls back to the scale entrance.
    bounce: {
      initial: { opacity: 0, scale: 0.98 },
      animate: { opacity: 1, scale: 1 },
      exit: { opacity: 0, scale: 0.98 },
    },
  };

  const arrowClasses = {
    top: "bottom-[-5px] left-1/2 transform -translate-x-1/2 rotate-45",
    bottom: "top-[-5px] left-1/2 transform -translate-x-1/2 rotate-45",
    left: "right-[-5px] top-1/2 transform -translate-y-1/2 rotate-45",
    right: "left-[-5px] top-1/2 transform -translate-y-1/2 rotate-45",
  };

  const arrowBorderClasses = {
    top: "border-b border-r",
    bottom: "border-t border-l",
    left: "border-t border-r",
    right: "border-b border-l",
  };

  return (
    <>
      {/* Tooltip trigger element */}
      <div
        ref={targetRef}
        className={`tooltip-trigger inline-block ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        {...props}
      >
        {children}
      </div>

      {/* Tooltip content */}
      {createPortal(
        <AnimatePresence>
          {isVisible && content && (
            <motion.div
              ref={tooltipRef}
              role="tooltip"
              initial={
                reduceMotion ? false : animationVariants[animation].initial
              }
              animate={animationVariants[animation].animate}
              exit={
                reduceMotion
                  ? { opacity: 0 }
                  : animationVariants[animation].exit
              }
              transition={{
                duration: reduceMotion ? 0 : 0.12,
                ease: [0.2, 0.8, 0.2, 1],
              }}
              className={`
                fixed z-[9999] px-3 py-2 text-xs rounded-lg
                ${getThemeClasses()}
                ${tooltipClassName}
                ${interactive ? "pointer-events-auto" : "pointer-events-none"}
              `}
              style={{
                top: `${tooltipPosition.top}px`,
                left: `${tooltipPosition.left}px`,
                maxWidth: `${maxWidth}px`,
              }}
              onMouseEnter={() => interactive && handleMouseEnter()}
              onMouseLeave={() => interactive && handleMouseLeave()}
            >
              
              <div className="relative">
                <div className="relative z-10">{content}</div>
              </div>

              {/* Arrow */}
              {arrow && (
                <div
                  className={`
                    absolute w-[10px] h-[10px]
                    ${arrowClasses[position]}
                    ${arrowBorderClasses[position]}
                    border-sg-line bg-sg-overlay
                  `}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
};

Tooltip.propTypes = {
  children: PropTypes.node.isRequired,
  content: PropTypes.node,
  position: PropTypes.oneOf(["top", "bottom", "left", "right"]),
  delay: PropTypes.number,
  arrow: PropTypes.bool,
  className: PropTypes.string,
  tooltipClassName: PropTypes.string,
  disabled: PropTypes.bool,
  maxWidth: PropTypes.number,
  triggerOnClick: PropTypes.bool,
  closeOnClickOutside: PropTypes.bool,
  theme: PropTypes.oneOf(["dark", "light", "primary", "glass", "gradient"]),
  glassMorphism: PropTypes.bool,
  animation: PropTypes.oneOf(["fade", "scale", "slide", "bounce"]),
  offset: PropTypes.number,
  interactive: PropTypes.bool,
};

export default Tooltip;
